import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  SurveyPageResponse,
  SurveyPageType,
  FormResponse,
} from '@employ-engage/data';
import {
  LoadingSpinner,
  RichTextRenderer,
  GET_FORM_RESPONSE_BY_ID_NO_AUTH,
  GET_FORM_BY_ID_AND_COMPANY_ID,
  GET_FORM_VERSION_BY_ID_AND_FRIENDLY_COMPANY_ID_NO_AUTH,
} from '@employ-engage/common-components';
import SurveyPageForm from '../survey-page-form/survey-page-form';

export function SurveyPage() {
  const { formId, employeeId, responseId, pageNumber } = useParams();
  const [currentPage, setCurrentPage] = useState<SurveyPageType | null>(null);
  const [currentResponse, setCurrentResponse] = useState<FormResponse | null>(
    null
  );
  const [versionData, setVersionData] = useState<any>(null);
  const [currentPageResponse, setCurrentPageResponse] =
    useState<SurveyPageResponse | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [templatePages, setTemplatePages] = useState<SurveyPageType[] | null>(
    null
  );
  const [completionButtonLabel, setCompletionButtonLabel] =
    useState<string>('');
  const { pathname } = useLocation();
  const apolloClient = useApolloClient();

  const friendlyCompanyId = pathname.split('/')[1];

  const {
    data: { formByIdAndCompanyIdNoAuth: form } = {},
    loading: formLoading,
  } = useQuery(GET_FORM_BY_ID_AND_COMPANY_ID, {
    variables: { id: formId, friendlyCompanyId },
    skip: !formId || !friendlyCompanyId,
    fetchPolicy: 'network-only',
  });

  const companyId = form?.companyId;

  const { data: { formResponseByIdNoAuth } = {}, loading: responseLoading } =
    useQuery(GET_FORM_RESPONSE_BY_ID_NO_AUTH, {
      variables: { id: responseId, employeeId, companyId },
      skip: !responseId || !employeeId || !companyId,
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    const fetchData = async () => {
      if (!form) return;

      const templateVersionId = form.templateVersionId;
      const {
        data: { formVersionByIdAndFriendlyCompanyIdNoAuth } = {},
      } = await apolloClient.query({
        query: GET_FORM_VERSION_BY_ID_AND_FRIENDLY_COMPANY_ID_NO_AUTH,
        variables: { id: templateVersionId, friendlyCompanyId },
        fetchPolicy: 'network-only',
      });

      setVersionData(formVersionByIdAndFriendlyCompanyIdNoAuth);

      if (formResponseByIdNoAuth) {
        setCurrentResponse(formResponseByIdNoAuth);
        const pageIdx = parseInt(pageNumber || '0', 10);
        setCurrentPageResponse(formResponseByIdNoAuth.pages?.[pageIdx] || null);
      }
    };
    if (formId && employeeId && responseId && form) fetchData();
    return () => {
      setCurrentResponse(null);
      setCurrentPageResponse(null);
    };
  }, [
    formResponseByIdNoAuth,
    pageNumber,
    apolloClient,
    employeeId,
    friendlyCompanyId,
    responseId,
    formId,
    form,
  ]);

  useEffect(() => {
    if (versionData) {
      const pages = versionData.pages;
      setNumPages(pages.length);
      setCurrentPage(pages[parseInt(pageNumber || '0', 10)]);
      setTemplatePages(pages);
      setCompletionButtonLabel(versionData.completionButtonLabel || '');
    }
    return () => {
      setCurrentPage(null);
    };
  }, [versionData, pageNumber]);

  if (formLoading || responseLoading) {
    return <LoadingSpinner spinnerId="survey-page-loading" />;
  }

  return (
    <div className="py-5">
      {currentPage ? (
        <div>
          {!!currentPage &&
          !!formId &&
          !!employeeId &&
          !!responseId &&
          !!pageNumber &&
          !!currentResponse &&
          !!templatePages ? (
            <>
              {currentPage?.welcomeText && (
                <div className="mb-5">
                  <RichTextRenderer content={currentPage.welcomeText} />
                </div>
              )}
              <SurveyPageForm
                completionButtonLabel={completionButtonLabel}
                templatePages={templatePages}
                currentResponse={currentResponse}
                setCurrentResponse={setCurrentResponse}
                currentPage={currentPage}
                currentPageResponse={currentPageResponse}
                setCurrentPageResponse={setCurrentPageResponse}
                isLastPage={parseInt(pageNumber || '0', 10) === numPages - 1}
                surveyId={formId}
                employeeId={employeeId}
                responseId={responseId}
                pageNumber={pageNumber}
              />
            </>
          ) : (
            <LoadingSpinner spinnerId="survey-page-loading" />
          )}
        </div>
      ) : (
        <LoadingSpinner spinnerId="survey-page-loading-2" />
      )}
    </div>
  );
}

export default SurveyPage;
